import { useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'
import { z } from 'zod'
import api from '@/admin/lib/api'
import { resetPassword } from '@/shared/lib/auth-api'
import { Logo } from '@/admin/components/ui/logo'
import { Button } from '@/shared/components/ui/button'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'

type FormValues = {
  password: string
  confirmPassword: string
}

const INVALID_LINK =
  'This reset link is invalid or has expired. Please request a new one.'

export function ResetPasswordPage() {
  // New headless links carry a single opaque `key`; legacy links (in-flight
  // emails sent before the headless cutover) carry `uid` + `token`.
  const { key, uid, token } = useParams<{
    key?: string
    uid?: string
    token?: string
  }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fields = useMemo<FormField[]>(() => [
    {
      type: 'password',
      name: 'password',
      label: 'New password',
      value: '',
      autoFocus: true,
      autoComplete: 'new-password',
      validationSchema: z.string().min(8, 'Password must be at least 8 characters'),
    },
    {
      type: 'password',
      name: 'confirmPassword',
      label: 'Confirm password',
      value: '',
      autoComplete: 'new-password',
      // Cross-field password match is validated server-side; the
      // generated per-field schema can't read sibling values.
      validationSchema: z.string().min(1, 'Please confirm your password'),
    },
  ], [])
  const { Form } = useForm(
    {
      name: 'reset-password-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as FormValues
      setLoading(true)
      setError(null)
      try {
        if (key) {
          // Headless reset. A successful reset that does not auto-login returns
          // 401 with no `errors`; an invalid/expired key (or a rejected
          // password) returns `errors`.
          const res = await resetPassword(key, values.password)
          if (res.errors?.length) {
            setError(res.errors[0]?.message || INVALID_LINK)
          } else {
            setSuccess(true)
          }
        } else {
          // Legacy uid/token reset (pre-cutover emails still in flight).
          await api.post('/auth/password-reset/confirm/', {
            uid,
            token,
            new_password: values.password,
          })
          setSuccess(true)
        }
      } catch {
        setError(INVALID_LINK)
      } finally {
        setLoading(false)
      }
    },
    [key, uid, token],
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] px-4">
      <div className="w-full max-w-[360px]">
        <div className="mb-8">
          <Logo size="md" />
        </div>

        {success ? (
          <div className="text-center">
            <CheckCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-ok)]" />
            <h2 className="mb-2 text-[20px] font-display text-[var(--ink)]">
              Password reset
            </h2>
            <p className="clara-small mb-6">
              Your password has been updated. You can now sign in.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="cursor-pointer rounded-[4px] bg-[var(--green)] px-6 py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)]"
            >
              Sign in
            </Button>
          </div>
        ) : error ? (
          <div className="text-center">
            <XCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-error)]" />
            <h2 className="mb-2 text-[20px] font-display text-[var(--ink)]">
              Link expired
            </h2>
            <p className="clara-small mb-6">{error}</p>
            <Button
              onClick={() => navigate('/forgot-password')}
              className="cursor-pointer rounded-[4px] bg-[var(--green)] px-6 py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)]"
            >
              Request new link
            </Button>
          </div>
        ) : (
          <>
            <h2 className="mb-1 text-[28px] leading-[1.1] font-display text-[var(--ink)]">
              Set new password
            </h2>
            <p className="clara-small mb-6">
              Choose a new password for your account.
            </p>

            <Form className="space-y-4" />
            <Button
              type="submit"
              form="reset-password-form"
              disabled={loading}
              className="mt-4 w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                'Reset password'
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
